import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard, FileText, CheckSquare, BookOpen,
    GraduationCap, KeyRound, Settings,
    AlertTriangle, Briefcase, Building2
} from 'lucide-react';
import { useTenant } from '@/context/TenantContext';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

interface NavItem {
    label: string;
    href: string;
    icon: React.ElementType;
    module?: string;
    roles?: string[]; // if set, only these roles see this item
}

const navItems: NavItem[] = [
    { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { label: 'Documents', href: '/documents', icon: FileText, module: 'documents' },
    { label: 'Checklists', href: '/checklists', icon: CheckSquare, module: 'checklists' },
    { label: 'Policies', href: '/policies', icon: BookOpen, module: 'policies' },
    { label: 'Training', href: '/training', icon: GraduationCap, module: 'training' },
    { label: 'Access', href: '/access', icon: KeyRound, module: 'access' },
    { label: 'Incidents', href: '/incidents', icon: AlertTriangle, module: 'incidents' },
    { label: 'Vendors', href: '/vendors', icon: Briefcase, module: 'vendors', roles: ['admin', 'manager'] },
    { label: 'Compliance', href: '/compliance', icon: Building2, module: 'compliance' },
];

interface SidebarProps {
    onNavigate?: () => void;
}

export default function Sidebar({ onNavigate }: SidebarProps) {
    const { hasModule, tenant } = useTenant();
    const { user } = useAuth();
    const location = useLocation();
    const userRole = user?.role || 'employee';

    let visibleItems = navItems.filter(
        (item) => (!item.module || hasModule(item.module)) && (!item.roles || item.roles.includes(userRole))
    );

    if (tenant?.plan === 'expired') {
        visibleItems = [];
    }

    return (
        <aside
            className="flex w-64 flex-col border-r border-gray-200 bg-white"
            role="navigation"
            aria-label="Main navigation"
        >
            {/* Logo / Brand */}
            <div className="flex h-16 items-center gap-2 border-b border-gray-200 px-6">
                <img src="/logo.png" alt="u-Compliance" className="h-7 w-7 object-contain" />
                <div>
                    <h1 className="text-lg font-bold text-gray-900">u-Compliance</h1>
                    {tenant && (
                        <p className="text-xs text-gray-500">{tenant.name}</p>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1 px-3 py-4" aria-label="Sidebar">
                <ul role="list" className="space-y-1">
                    {visibleItems.map((item) => (
                        <li key={item.href}>
                            <NavLink
                                to={item.href}
                                onClick={onNavigate}
                                className={({ isActive }) =>
                                    cn(
                                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors touch-target',
                                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                                        isActive
                                            ? 'bg-indigo-50 text-indigo-700'
                                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                    )
                                }
                                aria-current={location.pathname.startsWith(item.href) ? 'page' : undefined}
                            >
                                <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                                {item.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>

            {/* Settings at bottom */}
            <div className="border-t border-gray-200 px-3 py-4">
                <NavLink
                    to="/settings"
                    onClick={onNavigate}
                    className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors touch-target',
                        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        location.pathname === '/settings'
                            ? 'bg-indigo-50 text-indigo-700'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                    aria-current={location.pathname === '/settings' ? 'page' : undefined}
                >
                    <Settings className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    Settings
                </NavLink>
            </div>
        </aside>
    );
}